<template>
  <f7-page
    login-screen
    class="main-login"
  >
    <div class="text-align-center">
      <img
        width="280"
        height="auto"
        :src="appLogo"
        @click="emitClickLogo"
      >
    </div>

    <div>
      <f7-list form>
        <f7-list-input
          autofocus
          outline
          :label="t('common.user')"
          type="text"
          :placeholder="placeholderUser"
          clear-button
          name="username"
          :min="minUsernameLength"
          :max="maxUsernameLength"
          :error-message="usernameErr"
          :error-message-force="checkUsername(username)"
          required
          @input="username = $event.target.value"
          @change="checkBtn()"
        />

        <!-- Password -->
        <f7-list-input
          outline
          :label="t('common.password')"
          :type="passwordInputType"
          autocomplete="off"
          :placeholder="placeholderPass"
          clear-button
          name="password"
          :min="minPasswordLength"
          :max="maxPasswordLength"
          :error-message="passwordErr"
          :error-message-force="checkPassword(password)"
          required
          @input="password = $event.target.value"
          @change="checkBtn()"
          @keyup.enter="handleLogin"
        />

        <f7-list-item
          v-if="rememberMeVisivility"
          class="margin-left-half"
          checkbox
          :title="t('login.rememberMe')"
        />

        <f7-list-item
          v-if="showPasswordVisibility"
          class="margin-left-half"
          checkbox
          :title="t('login.showPassword')"
          @change="isPasswordVisible = $event.target.checked"
        />
      </f7-list>

      <f7-list>
        <f7-button
          col
          button
          large
          raised
          fill
          class="btn-bg-color"
          :style="customStyles"
          :disabled="btnDisable"
          @click="handleLogin"
        >
          {{ applyButtonText }}
        </f7-button>

        <div>
          <UCLectorNFC
            :active="allowNFCLogin && loginNeeded"
            @nfc-scan="handleNFCScan"
          />
        </div>
      </f7-list>
    </div>
  </f7-page>
</template>

<script setup>
// @ts-check
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { LOGO_HISPATEC } from '@/js/common/constants';
import { computed, ref } from 'vue';
import APIErp from '@/js/services/APIErp';
import { useI18n } from 'vue-i18n';

import UCLectorNFC from '@/components/common/UCLectorNFC.vue';
import { useSettingRef } from '@erpagroapp/settings';

const props = defineProps({
  titleButton: {
    type: String,
    default: null,
  },
  appLogo: {
    type: String,
    default: LOGO_HISPATEC,
  },
  placeholderUser: {
    type: String,
    default: null,
  },
  placeholderPass: {
    type: String,
    default: null,
  },
  minUserLengthErrorMsg: {
    type: String,
    default: null,
  },
  maxUserLengthErrorMsg: {
    type: String,
    default: null,
  },
  minPassLengthErrorMsg: {
    type: String,
    default: null,
  },
  maxPassLengthErrorMsg: {
    type: String,
    default: null,
  },
  preloaderDialogMessage: {
    type: String,
    default: null,
  },
  // Style
  btnBgColor: {
    type: String,
    default: 'var(--his-dark-blue)',
  },
  // Features
  rememberMeVisivility: {
    type: Boolean,
    default: true,
  },
  showPasswordVisibility: {
    type: Boolean,
    default: true,
  },
  // Format
  minUsernameLength: {
    type: Number,
    default: 1,
  },
  maxUsernameLength: {
    type: Number,
    default: 255,
  },
  minPasswordLength: {
    type: Number,
    default: 1,
  },
  maxPasswordLength: {
    type: Number,
    default: 255,
  },
  endPointForLogin: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits([
  'on-success',
  'click-logo',
]);

const { loginNeeded } = storeToRefs(useAppConfigStore());
const allowNFCLogin = useSettingRef('App.PermitirLoginNFC');
const { t } = useI18n();

const username = ref('');
const password = ref('');
const usernameErr = ref('');
const passwordErr = ref('');
const btnDisable = ref(true);
/** Determines whether to show the password or not (checkbox state). */
const isPasswordVisible = ref(false);

const customStyles = computed(() => ({
  '--custom-btn-bg-color': props.btnBgColor,
}));

const passwordInputType = computed(() => (isPasswordVisible.value ? 'text' : 'password'));
const applyButtonText = computed(() => props.titleButton ?? t('common.signIn'));

/**
 * Checks if the password is valid.
 * @param {string} passwordToCheck - The password to check.
 * @returns {boolean} `true` if the password is invalid, `false` otherwise.
 */
function checkPassword(passwordToCheck) {
  if (passwordToCheck.length < props.minPasswordLength) {
    passwordErr.value = props.minPassLengthErrorMsg ?? t('login.info.minLength');
    return true;
  }

  if (passwordToCheck.length > props.maxPasswordLength) {
    passwordErr.value = props.maxPassLengthErrorMsg ?? t('login.info.maxLength');
    return true;
  }

  return false;
}

/**
 * Checks if the username is valid.
 * @param {string} usernameToCheck - The username to check.
 * @returns {boolean} `true` if the username is invalid, `false` otherwise.
 */
function checkUsername(usernameToCheck) {
  if (usernameToCheck.length < props.minUsernameLength) {
    usernameErr.value = props.minUserLengthErrorMsg ?? t('login.info.minLength');
    return true;
  }

  if (usernameToCheck.length > props.maxUsernameLength) {
    usernameErr.value = props.maxUserLengthErrorMsg ?? t('login.info.maxLength');
    return true;
  }

  return false;
}

function checkBtn() {
  if (!checkUsername(username.value) && !checkPassword(password.value)) {
    btnDisable.value = false;
  } else {
    btnDisable.value = true;
  }
}

/**
 * Handles the NFC scan on login.
 * @param {string} tag - The NFC tag to scan.
 * @returns {Promise<void>}
 */
async function handleNFCScan(tag) {
  HiDialog.Preloader(true, t('login.info.validatingCard'));

  try {
    const result = await APIErp.callLoginNFC(tag);
    const mensaje = JSON.parse(result.Mensaje);

    username.value = mensaje.Usuario;

    emit('on-success', {
      userName: username.value,
      token: mensaje.Token,
    });
  } catch {
    const alertTitle = t('login.info.couldNotSignInWithCard');
    const alertText = t('login.info.notPossibleToSignIn');
    HiNotification.Alert(alertText, alertTitle);
  } finally {
    HiDialog.Preloader(false);

    username.value = '';
    password.value = '';
  }
}

async function handleLogin() {
  const preloaderDialogMessage = props.preloaderDialogMessage ?? t('login.info.signingIn');
  HiDialog.Preloader(true, preloaderDialogMessage);
  const response = await props.endPointForLogin(username.value, password.value);
  if (response?.status === 200) {
    emit('on-success', {
      userName: username.value,
      token: response.data,
    });
  }
  HiDialog.Preloader(false);
}

function emitClickLogo() {
  emit('click-logo');
}
</script>

<style scoped>
form {
  /* This will make the inputs take up the full width of the <li> element */
  --f7-list-item-padding-horizontal: 0;
}
</style>
